import { ColumnDimension } from "@components/common/data-grid/adm-data-grid-types";
import {
  SEARCH_PROJECTS_TABLE_COLUMN_LABELS,
  SearchProjectsTableColumnField,
  SearchProjectsTableGridColDef,
} from "@pages/home/search/search-tables/search-projects-tables/search-projects-table-types";
import { getMemberDisplayName } from "@utils/data-display";
import {
  ProjectAreaCell,
  ProjectCreatedAtCell,
  ProjectIdCell,
  ProjectManagerCell,
  ProjectNameCell,
  ProjectOptionsCell,
  ProjectStorageCell,
  ProjectWorkspaceCell,
  StandaloneCell,
} from "@utils/data-grid-utils/projects-data-grid-cells";
import { useMemo } from "react";

const columnDimension: ColumnDimension<SearchProjectsTableColumnField> = {
  name: {
    flex: 1.5,
  },
  id: {
    flex: 1.5,
  },
  area: {
    flex: 1,
  },
  storageSize: {
    flex: 1,
  },
  workspace: {
    flex: 2.5,
  },
  manager: {
    flex: 1,
  },
  createdAt: {
    flex: 1.5,
  },
  standalone: {
    flex: 0.5,
  },
  options: {
    flex: 1,
    maxWidth: 120,
  },
};

/**
 * @returns the columns definition to be used in the search projects table
 */
export function useSearchProjectsTableColumns(): SearchProjectsTableGridColDef[] {
  return useMemo(() => {
    const columnsMap: Record<
      SearchProjectsTableColumnField,
      SearchProjectsTableGridColDef
    > = {
      name: {
        field: "name",
        headerName: SEARCH_PROJECTS_TABLE_COLUMN_LABELS.name,
        ...columnDimension.name,
        valueGetter: (_, row) => row.entity.name,
        renderCell: ProjectNameCell,
      },

      id: {
        field: "id",
        headerName: SEARCH_PROJECTS_TABLE_COLUMN_LABELS.id,
        ...columnDimension.id,
        getApplyQuickFilterFn: () => null,
        valueGetter: (_, row) => row.entity.id,
        renderCell: ProjectIdCell,
      },

      area: {
        field: "area",
        headerName: SEARCH_PROJECTS_TABLE_COLUMN_LABELS.area,
        ...columnDimension.area,
        getApplyQuickFilterFn: () => null,
        valueGetter: (_, row) => row.entity.area.amount,
        renderCell: ProjectAreaCell,
      },

      storageSize: {
        field: "storageSize",
        headerName: SEARCH_PROJECTS_TABLE_COLUMN_LABELS.storageSize,
        ...columnDimension.storageSize,
        valueGetter: (_, row) => row.entity.storage?.size,
        renderCell: ProjectStorageCell,
      },

      workspace: {
        field: "workspace",
        headerName: SEARCH_PROJECTS_TABLE_COLUMN_LABELS.workspace,
        ...columnDimension.workspace,
        renderCell: ProjectWorkspaceCell,
      },

      manager: {
        field: "manager",
        headerName: SEARCH_PROJECTS_TABLE_COLUMN_LABELS.manager,
        ...columnDimension.manager,
        valueGetter: (_, row) =>
          getMemberDisplayName(row.entity.projectManager),
        renderCell: ProjectManagerCell,
      },

      createdAt: {
        field: "createdAt",
        headerName: SEARCH_PROJECTS_TABLE_COLUMN_LABELS.createdAt,
        ...columnDimension.createdAt,
        getApplyQuickFilterFn: () => null,
        valueGetter: (_, row) => row.entity.createdAt,
        renderCell: ProjectCreatedAtCell,
      },

      standalone: {
        field: "standalone",
        headerName: SEARCH_PROJECTS_TABLE_COLUMN_LABELS.standalone,
        ...columnDimension.standalone,
        getApplyQuickFilterFn: () => null,
        renderCell: StandaloneCell,
      },

      options: {
        field: "options",
        type: "actions",
        headerName: SEARCH_PROJECTS_TABLE_COLUMN_LABELS.options,
        ...columnDimension.options,
        getApplyQuickFilterFn: () => null,
        align: "right",
        renderCell: (params) =>
          ProjectOptionsCell(params, "search-projects-table"),
      },
    };
    return Object.values(columnsMap);
  }, []);
}
